import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import copyToClipboard from 'util/clipboard/copyToClipboard';

export interface PublicKeySectionProps {
  value: string | null;
  onChangePublicKey: (newPublicKey: string) => void;
}

interface ClipboardButtonProps {
  getValue: () => string;
}

export function ClipboardButton(props: ClipboardButtonProps): JSX.Element | null {
  return (
    <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy public key">
      <Button
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        size={ButtonSize.MINI}
        className={css(styles.upperRight)}
        icon="copy"
        onClick={() =>
          copyToClipboard(props.getValue(), {
            showToastOnSuccess: true,
            toastText: 'Copied public key to clipboard.',
          })
        }
      />
    </Tooltip>
  );
}

export default function PublicKeySection(props: PublicKeySectionProps): JSX.Element | null {
  function getValue(): string {
    return props.value ?? '';
  }

  return (
    <InputWrapper
      id={_.uniqueId('benchlingAppPublicKey')}
      label="Public Key"
      labelStyle={FieldLabel.STYLES.LABEL}
      help="This key is used to encrypt values stored securely in app configs."
      helpInteraction={FloatingInteraction.HOVER}
    >
      <div className={css(styles.publicKey)}>
        <EditableTextField
          inputClassName={css(styles.editableTextFieldClass)}
          emptyValueAllowed={true}
          placeholder="Public Key for encrypting secure_text app config items"
          value={props.value ?? ''}
          requestChange={props.onChangePublicKey}
          type={TextFieldType.TEXTAREA}
        />
      </div>
      {props.value != null && <ClipboardButton getValue={getValue} />}
    </InputWrapper>
  );
}

const LARGE_TEXTAREA_HEIGHT = 180;
const styles = StyleSheet.create({
  sectionDescription: {
    marginBottom: '15px',
  },
  textParent: {
    position: 'relative',
  },
  upperRight: {
    position: 'absolute',
    right: '1em',
    top: '1em',
  },
  editableTextFieldClass: {
    minHeight: LARGE_TEXTAREA_HEIGHT,
    width: '270px',
  },
  publicKey: {
    minWidth: '270px',
    overflow: 'scroll',
  },
});
